/**
 * RequestDetailsModal Component
 *
 * Modal for viewing full details of an advisory request
 */

import React, { useState, useEffect } from 'react';

import { format } from 'date-fns';

import { Box } from '@mui/material';

import StatusBadge from './StatusBadge';
import * as S from './styles';
import UserDisplay from './UserDisplay';
import { useAdvisoryRequests } from '../hooks/useAdvisoryRequests';
import Modal from 'components/Modal';
import Spin from 'components/Spin';
import Text from 'components/Text';
import { LeadRequestWithUserData } from 'types/leadRequestsTypes';

interface RequestDetailsModalProps {
  request: LeadRequestWithUserData | null;
  isOpen: boolean;
  onClose: () => void;
  onCreateLink: (request: LeadRequestWithUserData) => void;
}

const RequestDetailsModal: React.FC<RequestDetailsModalProps> = ({
  request,
  isOpen,
  onClose,
  onCreateLink,
}) => {
  const [requestData, setRequestData] = useState<LeadRequestWithUserData | null>(request);
  const [loading, setLoading] = useState(false);
  const { fetchRequestDetails } = useAdvisoryRequests();

  useEffect(() => {
    if (!request) return;
    const fetchRequestData = async () => {
      setLoading(true);
      try {
        const response = await fetchRequestDetails(request.id);
        setRequestData(response);
      } catch (error) {
        console.error('Error fetching request details:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRequestData();
  }, [request]);

  const handleClose = () => {
    setRequestData(null);
    onClose();
  };

  if (loading) return <Spin />;
  if (!requestData) return null;

  const renderFormResponses = () => {
    if (!requestData.form_responses) {
      return <Text color="textSecondary">Nenhum dado do formulário disponível</Text>;
    }

    if (
      typeof requestData.form_responses === 'object' &&
      !Array.isArray(requestData.form_responses)
    ) {
      return (
        <S.FormDataDisplay>
          {Object.entries(requestData.form_responses).map(([key, value]) => (
            <S.FormDataRow key={key}>
              <S.FormDataLabel>{key}</S.FormDataLabel>
              <S.FormDataValue>{String(value)}</S.FormDataValue>
            </S.FormDataRow>
          ))}
        </S.FormDataDisplay>
      );
    }

    return (
      <Text color="textSecondary">Dados do formulário: {JSON.stringify(requestData.form_responses)}</Text>
    );
  };

  const renderInvitationLinks = () => {
    if (!requestData.invitation_links || requestData.invitation_links.length === 0) {
      return <Text color="textSecondary">Nenhum link de convite criado ainda</Text>;
    }

    return (
      <Box>
        {requestData.invitation_links.map((link, index) => (
          <S.FormDataDisplay key={link.id}>
            <S.FormDataRow>
              <S.FormDataLabel>Link #{index + 1}</S.FormDataLabel>
              <S.FormDataValue>
                {window.location.origin}/register?invite_id={link.link}
              </S.FormDataValue>
            </S.FormDataRow>
            <S.FormDataRow>
              <S.FormDataLabel>Status</S.FormDataLabel>
              <S.FormDataValue>
                <StatusBadge status={link.status} size="small" />
              </S.FormDataValue>
            </S.FormDataRow>
            {link.invite_data?.invitee_first_name && link.invite_data?.invitee_last_name && (
              <S.FormDataRow>
                <S.FormDataLabel>Nome do convidado:</S.FormDataLabel>
                <S.FormDataValue>
                  {link.invite_data?.invitee_first_name} {link.invite_data?.invitee_last_name}
                </S.FormDataValue>
              </S.FormDataRow>
            )}
            {link.invite_data?.invitee_email && (
              <S.FormDataRow>
                <S.FormDataLabel>Email do convidado :</S.FormDataLabel>
                <S.FormDataValue>{link.invite_data?.invitee_email}</S.FormDataValue>
              </S.FormDataRow>
            )}
            {link.invite_data?.invitee_cell_phone && (
              <S.FormDataRow>
                <S.FormDataLabel>Telefone do convidado :</S.FormDataLabel>
                <S.FormDataValue>{link.invite_data?.invitee_cell_phone}</S.FormDataValue>
              </S.FormDataRow>
            )}
            <S.FormDataRow>
              <S.FormDataLabel>Criado em:</S.FormDataLabel>
              <S.FormDataValue>
                {format(new Date(link.created_at), 'dd/MM/yyyy HH:mm')}
              </S.FormDataValue>
            </S.FormDataRow>
          </S.FormDataDisplay>
        ))}
      </Box>
    );
  };

  return (
    <Modal
      closeModal={handleClose}
      title="Detalhes da solicitação"
      onSecondaryClick={handleClose}
      secondaryTextButton="Fechar"
      onPrimaryClick={() => onCreateLink(requestData as LeadRequestWithUserData)}
      primaryTextButton="Criar link de convite"
    >
      <S.ModalContent>
        {/* Request Info */}
        <S.ModalSection>
          <S.ModalSectionTitle>Informações da solicitação</S.ModalSectionTitle>
          <S.FormDataDisplay>
            <S.FormDataRow>
              <S.FormDataLabel>ID da solicitação</S.FormDataLabel>
              <S.FormDataValue>{requestData.id}</S.FormDataValue>
            </S.FormDataRow>
            <S.FormDataRow>
              <S.FormDataLabel>Status:</S.FormDataLabel>
              <S.FormDataValue>
                <StatusBadge status={requestData.status} />
              </S.FormDataValue>
            </S.FormDataRow>
            <S.FormDataRow>
              <S.FormDataLabel>Solicitante:</S.FormDataLabel>
              <S.FormDataValue>
                <UserDisplay userData={requestData.requester_user_data} size="small" showEmail />
              </S.FormDataValue>
            </S.FormDataRow>
            {/* {requestData.lead_user_data && (
              <S.FormDataRow>
                <S.FormDataLabel>Lead:</S.FormDataLabel>
                <S.FormDataValue>
                  <UserDisplay userData={requestData.lead_user_data} size="small" showEmail />
                </S.FormDataValue>
              </S.FormDataRow>
            )} */}
            <S.FormDataRow>
              <S.FormDataLabel>Criado em:</S.FormDataLabel>
              <S.FormDataValue>
                {format(new Date(requestData.created_at), 'dd/MM/yyyy HH:mm')}
              </S.FormDataValue>
            </S.FormDataRow>
            <S.FormDataRow>
              <S.FormDataLabel>Atualizado em:</S.FormDataLabel>
              <S.FormDataValue>
                {format(new Date(requestData.updated_at), 'dd/MM/yyyy HH:mm')}
              </S.FormDataValue>
            </S.FormDataRow>
          </S.FormDataDisplay>
        </S.ModalSection>

        {/* Form Responses */}
        <S.ModalSection>
          <S.ModalSectionTitle>Respostas do formulário</S.ModalSectionTitle>
          {renderFormResponses()}
        </S.ModalSection>

        {/* Invitation Links */}
        <S.ModalSection>
          <S.ModalSectionTitle>Links de convite</S.ModalSectionTitle>
          {renderInvitationLinks()}
        </S.ModalSection>
      </S.ModalContent>
    </Modal>
  );
};

export default RequestDetailsModal;
